/**
 * Timer Controls 按钮控制逻辑
 * 只负责管理 Timer 特有的按钮 (Start/Pause/Reset/Split)
 * 全屏和保持常亮功能由 FullscreenManager 处理
 */

const BUTTON_ICONS = {
    start: `<svg class="action-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M5 5a2 2 0 0 1 3.008-1.728l11.997 6.998a2 2 0 0 1 .003 3.458l-12 7A2 2 0 0 1 5 19z"></path>
    </svg>`,
    pause: `<svg class="action-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="currentColor">
        <rect x="6" y="3" width="3" height="18" rx="1"></rect>
        <rect x="15" y="3" width="3" height="18" rx="1"></rect>
    </svg>`,
    reset: `<svg class="action-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
        <path d="M3 3v5h5"></path>
    </svg>`,
    split: `<svg class="action-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M6 22V2.8a.8.8 0 0 1 1.17-.71l11.38 5.69a.8.8 0 0 1 0 1.44L6 15.5"></path>
    </svg>`
};

const BUTTON_LABELS = {
    start: 'Start',
    pause: 'Pause',
    reset: 'Reset',
    split: 'Split'
};

class TimerControls {
    constructor() {
        this.startPauseBtn = document.getElementById('start-pause-btn');
        this.resetSplitBtn = document.getElementById('reset-split-btn');
        this.stopAlarmBtn = document.getElementById('stop-alarm-btn');

        this.callbacks = {
            onStartPause: null,
            onResetSplit: null,
            onStopAlarm: null
        };

        this._initEventListeners();
    }

    /**
     * 初始化事件监听
     */
    _initEventListeners() {
        // Start/Pause 按钮
        if (this.startPauseBtn) {
            this.startPauseBtn.addEventListener('click', () => {
                const action = this.startPauseBtn.getAttribute('data-action');
                this._triggerCallback('onStartPause', action);
            });
        }

        // Reset/Split 按钮
        if (this.resetSplitBtn) {
            this.resetSplitBtn.addEventListener('click', () => {
                const action = this.resetSplitBtn.getAttribute('data-action');
                this._triggerCallback('onResetSplit', action);
            });
        }

        // Stop Alarm 按钮
        if (this.stopAlarmBtn) {
            this.stopAlarmBtn.addEventListener('click', () => {
                this._triggerCallback('onStopAlarm');
            });
        }
    }

    _setButtonAppearance(button, type) {
        if (!button) return;
        const label = BUTTON_LABELS[type] || 'Action';
        const icon = BUTTON_ICONS[type] || '';
        button.innerHTML = `${icon}<span class="sr-only">${label}</span>`;
        button.setAttribute('aria-label', label);
        button.setAttribute('title', label);

        button.classList.remove('is-start', 'is-icon-only');
        if (type === 'start') {
            button.classList.add('is-start');
        } else {
            button.classList.add('is-icon-only');
        }
    }

    /**
     * 更新按钮状态 - Stopwatch 模式
     * @param {string} state - 'idle' | 'running' | 'paused'
     */
    updateButtonsForStopwatch(state) {
        if (!this.startPauseBtn || !this.resetSplitBtn) return;

        switch (state) {
            case 'idle':
                // 初始状态: 只显示 Start 按钮
                this.startPauseBtn.setAttribute('data-action', 'start');
                this._setButtonAppearance(this.startPauseBtn, 'start');
                this.resetSplitBtn.style.display = 'none';
                break;

            case 'running':
                // 运行中: Pause 和 Split 按钮
                this.startPauseBtn.setAttribute('data-action', 'pause');
                this._setButtonAppearance(this.startPauseBtn, 'pause');
                this.resetSplitBtn.setAttribute('data-action', 'split');
                this._setButtonAppearance(this.resetSplitBtn, 'split');
                this.resetSplitBtn.style.display = 'inline-flex';
                break;

            case 'paused':
                // 暂停: Start 和 Reset 按钮
                this.startPauseBtn.setAttribute('data-action', 'start');
                this._setButtonAppearance(this.startPauseBtn, 'start');
                this.resetSplitBtn.setAttribute('data-action', 'reset');
                this._setButtonAppearance(this.resetSplitBtn, 'reset');
                this.resetSplitBtn.style.display = 'inline-flex';
                break;
        }
    }

    /**
     * 更新按钮状态 - Countdown 模式
     * @param {string} state - 'idle' | 'running' | 'paused'
     */
    updateButtonsForCountdown(state) {
        if (!this.startPauseBtn || !this.resetSplitBtn) return;

        switch (state) {
            case 'idle':
                this.startPauseBtn.setAttribute('data-action', 'start');
                this.resetSplitBtn.setAttribute('data-action', 'reset');
                this._setButtonAppearance(this.startPauseBtn, 'start');
                this._setButtonAppearance(this.resetSplitBtn, 'reset');
                this.resetSplitBtn.style.display = 'none';
                break;

            case 'running':
                this.startPauseBtn.setAttribute('data-action', 'pause');
                this.resetSplitBtn.setAttribute('data-action', 'reset');
                this._setButtonAppearance(this.startPauseBtn, 'pause');
                this._setButtonAppearance(this.resetSplitBtn, 'reset');
                this.resetSplitBtn.style.display = 'inline-flex';
                break;

            case 'paused':
                this.startPauseBtn.setAttribute('data-action', 'start');
                this.resetSplitBtn.setAttribute('data-action', 'reset');
                this._setButtonAppearance(this.startPauseBtn, 'start');
                this._setButtonAppearance(this.resetSplitBtn, 'reset');
                this.resetSplitBtn.style.display = 'inline-flex';
                break;
        }
    }

    /**
     * 设置停止铃声按钮显示状态
     * @param {boolean} show - 是否显示
     */
    setStopAlarmVisible(show) {
        if (!this.stopAlarmBtn) return;
        this.stopAlarmBtn.style.display = show ? 'inline-flex' : 'none';
    }

    /**
     * 注册回调函数
     */
    on(event, callback) {
        if (this.callbacks.hasOwnProperty(event)) {
            this.callbacks[event] = callback;
        }
    }

    /**
     * 触发回调函数
     */
    _triggerCallback(event, data) {
        if (this.callbacks[event] && typeof this.callbacks[event] === 'function') {
            this.callbacks[event](data);
        }
    }

    /**
     * 销毁
     */
    destroy() {
        this.callbacks = {};
    }
}

// 导出
export default TimerControls;
